import React, { useContext, useEffect, useMemo, useState } from 'react';
import '../css/govuk-frontend.min.css';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import api from '../axiosInstance';
import AuthContext from '../AuthContext';
import { LanguageContext } from '../LanguageContext';
import { resolveMessageComposeUnavailableReason } from './messageComposeAvailability';

const FOLDERS = ['inbox', 'sent', 'deleted'];

const copy = {
  en: {
    title: 'Secure messages',
    myDocuments: 'My documents',
    documentsAction_one: 'You have 1 document waiting for your review or signature.',
    documentsAction_other: (count) => `You have ${count} documents waiting for your review or signature.`,
    documentsAvailable_one: 'You have 1 document available in your record.',
    documentsAvailable_other: (count) => `You have ${count} documents available in your record.`,
    documentsCta: 'View documents',
    inbox: 'Inbox',
    sent: 'Sent items',
    deleted: 'Deleted items',
    newMessage: 'New message',
    clearDeleted: 'Clear deleted items',
    confirmClear: 'Are you sure you want to permanently delete all items in Deleted?',
    newMessageCaseUnavailable: 'You can send a new message once your portal account is linked to your current case.',
    newMessageParticipantUnavailable: 'We cannot confirm that your portal account matches the participant linked to this case. Contact program support.',
    newMessageAssigneeUnavailable: 'You can send a new message once a program team member is assigned to your current case.',
    newMessageContextUnavailable: 'New messaging is unavailable while we confirm your current case and assigned program contact.',
    noMessages: 'You have no messages in this folder.',
    loading: 'Loading messages...',
    loadError: 'We could not load your messages. Please try again.',
    subject: 'Subject',
    from: 'From',
    to: 'To',
    status: 'Status',
    date: 'Date',
    unread: 'Unread',
    read: 'Read',
    replied: 'Replied',
    deletedStatus: 'Deleted',
    urgent: 'Urgent',
    sentLabel: 'Sent',
    receivedLabel: 'Received',
    notification: 'Your message has been sent.',
    backToDashboard: 'Back to dashboard',
    you: 'You',
    programTeam: 'Program team'
  },
  fr: {
    title: 'Messages sécurisés',
    myDocuments: 'Mes documents',
    documentsAction_one: 'Vous avez 1 document en attente de votre révision ou de votre signature.',
    documentsAction_other: (count) => `Vous avez ${count} documents en attente de votre révision ou de votre signature.`,
    documentsAvailable_one: 'Vous avez 1 document disponible dans votre dossier.',
    documentsAvailable_other: (count) => `Vous avez ${count} documents disponibles dans votre dossier.`,
    documentsCta: 'Voir les documents',
    inbox: 'Boîte de réception',
    sent: 'Messages envoyés',
    deleted: 'Messages supprimés',
    newMessage: 'Nouveau message',
    clearDeleted: 'Vider les éléments supprimés',
    confirmClear: 'Voulez-vous vraiment supprimer définitivement tous les éléments du dossier Supprimés ?',
    newMessageCaseUnavailable: 'Vous pourrez envoyer un nouveau message lorsque votre compte du portail sera lié à votre dossier actuel.',
    newMessageParticipantUnavailable: 'Nous ne pouvons pas confirmer que votre compte du portail correspond à la personne liée à ce dossier. Communiquez avec le soutien du programme.',
    newMessageAssigneeUnavailable: 'Vous pourrez envoyer un nouveau message lorsqu’un membre de l’équipe du programme sera affecté à votre dossier actuel.',
    newMessageContextUnavailable: 'La nouvelle messagerie est indisponible pendant que nous confirmons votre dossier actuel et la personne-ressource du programme.',
    noMessages: 'Aucun message dans ce dossier.',
    loading: 'Chargement des messages…',
    loadError: 'Impossible de charger vos messages. Veuillez réessayer.',
    subject: 'Objet',
    from: 'De',
    to: 'À',
    status: 'Statut',
    date: 'Date',
    unread: 'Non lu',
    read: 'Lu',
    replied: 'Répondu',
    deletedStatus: 'Supprimé',
    urgent: 'Urgent',
    sentLabel: 'Envoyé',
    receivedLabel: 'Reçu',
    notification: 'Votre message a été envoyé.',
    backToDashboard: 'Retour au tableau de bord',
    you: 'Vous',
    programTeam: 'Équipe du programme'
  }
};

const MESSAGE_STYLES = `
  .app-messages-list { margin-top: 1.5rem; }
  .app-message-status-tags { display: flex; gap: 8px; align-items: center; }
  /* Minimal fallback styles for GOV.UK tabs when global CSS is missing */
  .govuk-tabs { margin-top: 10px; }
  .govuk-tabs__list { list-style: none; margin: 0; padding: 0; display: flex; gap: 8px; flex-wrap: wrap; }
  .govuk-tabs__list-item { margin: 0; list-style: none; }
  .govuk-tabs__list-item::marker { content: none; }
  .govuk-tabs__list-item::before { content: none !important; }
  .govuk-tabs__tab { display: inline-block; padding: 8px 16px; border: 1px solid #b1b4b6; background: #f3f2f1; color: #1d70b8; text-decoration: none; }
  .govuk-tabs__tab:hover { background: #e7e6e6; }
  .govuk-tabs__list-item--selected .govuk-tabs__tab { background: #fff; border-bottom: 1px solid #fff; position: relative; z-index: 1; color: #0b0c0c; }
  .govuk-tabs__panel { border: 1px solid #b1b4b6; padding: 16px; margin-top: -1px; background: #fff; }
  .govuk-tabs__panel--hidden { display: none; }
`;

const cx = (...classes) => classes.filter(Boolean).join(' ');

const formatDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const mapStatusLabel = (message, t) => {
  if (message.deleted) return t.deletedStatus;
  if (message.status === 'replied') return t.replied;
  if (message.direction === 'received') {
    return message.status === 'unread' ? t.unread : t.read;
  }
  return t.sentLabel;
};

const resolveNewMessageUnavailableHint = (context, t) => {
  const reason = resolveMessageComposeUnavailableReason(context);
  if (reason === 'participant') return t.newMessageParticipantUnavailable;
  if (reason === 'case') return t.newMessageCaseUnavailable;
  if (reason === 'assignee') return t.newMessageAssigneeUnavailable;
  if (reason === 'staff_mapping' || reason === 'context') return t.newMessageContextUnavailable;
  return null;
};

const Messages = () => {
  const { user } = useContext(AuthContext);
  const { language } = useContext(LanguageContext);
  const t = useMemo(() => copy[language] || copy.en, [language]);
  const location = useLocation();
  const navigate = useNavigate();

  const [context, setContext] = useState({ loading: true, hasSubmittedApplication: false, canCompose: false });
  const [messagesByFolder, setMessagesByFolder] = useState({ inbox: [], sent: [], deleted: [] });
  const [documentsSummary, setDocumentsSummary] = useState({ totalCount: 0, activeCount: 0 });
  const [activeTab, setActiveTab] = useState('inbox');
  const [globalLoading, setGlobalLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNotification, setShowNotification] = useState(Boolean(location.state?.messageSent));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (location.state?.messageSent) {
      navigate(location.pathname, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const loadData = async () => {
    if (!user) return;
    setGlobalLoading(true);
    setError('');
    try {
      const [ctxResult, inboxResult, sentResult, deletedResult, documentsResult] = await Promise.allSettled([
        api.get('/api/messages/context'),
        api.get('/api/messages', { params: { folder: 'inbox' } }),
        api.get('/api/messages', { params: { folder: 'sent' } }),
        api.get('/api/messages', { params: { folder: 'deleted' } }),
        api.get('/api/signing-requests/summary')
      ]);
      if (
        ctxResult.status !== 'fulfilled' ||
        inboxResult.status !== 'fulfilled' ||
        sentResult.status !== 'fulfilled' ||
        deletedResult.status !== 'fulfilled'
      ) {
        throw new Error('messages_load_failed');
      }
      setContext({ loading: false, ...ctxResult.value.data });
      setMessagesByFolder({
        inbox: Array.isArray(inboxResult.value.data?.items) ? inboxResult.value.data.items : [],
        sent: Array.isArray(sentResult.value.data?.items) ? sentResult.value.data.items : [],
        deleted: Array.isArray(deletedResult.value.data?.items) ? deletedResult.value.data.items : []
      });
      if (documentsResult.status === 'fulfilled') {
        setDocumentsSummary({
          totalCount: Number(documentsResult.value.data?.totalCount) || 0,
          activeCount: Number(documentsResult.value.data?.activeCount) || 0,
        });
      } else {
        setDocumentsSummary({ totalCount: 0, activeCount: 0 });
      }
    } catch (err) {
      console.error('Failed to load messaging data', err);
      setError(t.loadError);
    } finally {
      setGlobalLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const messagesForActiveTab = messagesByFolder[activeTab] || [];
  const documentSummaryText = documentsSummary.activeCount === 1
    ? t.documentsAction_one
    : documentsSummary.activeCount > 1
    ? t.documentsAction_other(documentsSummary.activeCount)
    : documentsSummary.totalCount === 1
    ? t.documentsAvailable_one
    : t.documentsAvailable_other(documentsSummary.totalCount);
  const newMessageUnavailableHint = resolveNewMessageUnavailableHint(context, t);

  const renderTableRows = () => (
    <tbody className="govuk-table__body">
      {messagesForActiveTab.map((message) => {
        const subject = message.subject || t.subject;
        const statusLabel = mapStatusLabel(message, t);
        const directionLabel = message.direction === 'sent' ? t.sentLabel : t.receivedLabel;
        return (
          <tr key={message.id} className="govuk-table__row">
            <td className="govuk-table__cell">
              <Link className="govuk-link" to={`/messages/${message.id}`}>{subject}</Link>
              {' '}
              {message.urgent ? (<strong className="govuk-tag govuk-tag--red govuk-!-margin-left-1">{t.urgent}</strong>) : null}
            </td>
            <td className="govuk-table__cell">{directionLabel}</td>
            <td className="govuk-table__cell">
              <div className="app-message-status-tags">
                <span>{statusLabel}</span>
              </div>
            </td>
            <td className="govuk-table__cell">{formatDateTime(message.created_at)}</td>
          </tr>
        );
      })}
    </tbody>
  );

  const renderTable = () => (
    <table className="govuk-table">
      <thead className="govuk-table__head">
        <tr className="govuk-table__row">
          <th scope="col" className="govuk-table__header">{t.subject}</th>
          <th scope="col" className="govuk-table__header">{t.from}</th>
          <th scope="col" className="govuk-table__header">{t.status}</th>
          <th scope="col" className="govuk-table__header">{t.date}</th>
        </tr>
      </thead>
      {renderTableRows()}
    </table>
  );

  return (
    <div className="govuk-width-container govuk-!-margin-top-3">
      <style>{MESSAGE_STYLES}</style>
      <main className="govuk-main-wrapper" role="main">
        <h1 className="govuk-heading-l">{t.title}</h1>
        {documentsSummary.totalCount > 0 && (
          <section className="app-documents-callout govuk-!-margin-bottom-5" aria-labelledby="messages-documents-title">
            <div>
              <h2 className="govuk-heading-m govuk-!-margin-bottom-2" id="messages-documents-title">{t.myDocuments}</h2>
              <p className="govuk-body govuk-!-margin-bottom-0">{documentSummaryText}</p>
            </div>
            <div className="app-documents-callout__actions">
              <Link className="govuk-button govuk-!-margin-bottom-0" to="/documents">{t.documentsCta}</Link>
            </div>
          </section>
        )}
        {error && (
          <div className="govuk-error-summary" role="alert" aria-live="assertive">
            <h2 className="govuk-error-summary__title">{error}</h2>
          </div>
        )}
        {showNotification && (
          <div className="govuk-notification-banner" role="status" data-module="govuk-notification-banner">
            <div className="govuk-notification-banner__content">
              <p className="govuk-notification-banner__heading">{t.notification}</p>
            </div>
            <button type="button" className="govuk-notification-banner__link" onClick={() => setShowNotification(false)}>
              x
            </button>
          </div>
        )}
        <div className="govuk-tabs" data-module="govuk-tabs">
          <h2 className="govuk-tabs__title govuk-visually-hidden">{t.title}</h2>
          <ul className="govuk-tabs__list" role="tablist">
            {FOLDERS.map((folder) => {
              const tabId = `tab_${folder}`;
              const panelId = `panel_${folder}`;
              const selected = activeTab === folder;
              return (
                <li
                  key={folder}
                  className={cx('govuk-tabs__list-item', selected && 'govuk-tabs__list-item--selected')}
                  role="presentation"
                >
                  <a
                    href={`#${panelId}`}
                    id={tabId}
                    className="govuk-tabs__tab"
                    role="tab"
                    aria-controls={panelId}
                    aria-selected={selected}
                    tabIndex={selected ? 0 : -1}
                    onClick={(e) => { e.preventDefault(); setActiveTab(folder); }}
                  >
                    {t[folder]}
                  </a>
                </li>
              );
            })}
          </ul>

          {FOLDERS.map((folder) => {
            const selected = activeTab === folder;
            const panelId = `panel_${folder}`;
            const tabId = `tab_${folder}`;
            const list = messagesByFolder[folder] || [];
            return (
              <div
                key={folder}
                className={cx('govuk-tabs__panel', !selected && 'govuk-tabs__panel--hidden')}
                id={panelId}
                role="tabpanel"
                aria-labelledby={tabId}
              >
                <div className="govuk-button-group govuk-!-margin-bottom-3">
                  {folder !== 'deleted' ? (
                    <Link
                      to="/messages/new"
                      className="govuk-button"
                      aria-disabled={!context.canCompose || globalLoading}
                      onClick={(event) => {
                        if (!context.canCompose || globalLoading) {
                          event.preventDefault();
                        }
                      }}
                    >
                      {t.newMessage}
                    </Link>
                  ) : (
                    <button
                      type="button"
                      className="govuk-button govuk-button--secondary"
                      disabled={globalLoading || busy}
                      onClick={async () => {
                        if (!window.confirm(t.confirmClear)) return;
                        try {
                          setBusy(true);
                          await api.delete('/api/messages/clear-deleted');
                          setMessagesByFolder(prev => ({ ...prev, deleted: [] }));
                        } catch (e) {
                          console.error('Failed to clear deleted items', e);
                          alert(t.loadError);
                        } finally {
                          setBusy(false);
                        }
                      }}
                    >
                      {t.clearDeleted}
                    </button>
                  )}
                </div>
                {selected && folder !== 'deleted' && newMessageUnavailableHint ? (
                  <p className="govuk-hint">{newMessageUnavailableHint}</p>
                ) : null}
                {globalLoading ? (
                  <p className="govuk-body">{t.loading}</p>
                ) : list.length === 0 ? (
                  <p className="govuk-body">{t.noMessages}</p>
                ) : (
                  <div className="app-messages-list">
                    {selected ? renderTable() : null}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <p className="govuk-body govuk-!-margin-top-6">
          <Link to="/dashboard" className="govuk-link">
            {t.backToDashboard}
          </Link>
        </p>
      </main>
    </div>
  );
};

export default Messages;
